import React from "react";
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from "@chakra-ui/react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import {
  white,
  newBorder,
  textPrimary,
  primary,
  textSecondary,
  hoverItemColor,
} from "../../utils/colors";
import { shade } from "polished";

const currencyOptions = [
  {
    value: "USD",
    label: "USD – US Dollar",
    flag: "https://flagcdn.com/us.svg",
  },
  {
    value: "EUR",
    label: "EUR – Euro",
    flag: "https://flagcdn.com/eu.svg", // EU flag
  },
  {
    value: "GBP",
    label: "GBP – British Pound",
    flag: "https://flagcdn.com/gb.svg",
  },
];

const FormControlCurrency = ({
  autoFocus,
  label,
  value,
  onChange,
  isRequired,
  isTouched,
  isError,
  errorMessage,
  isDisabled,
  w,
  m,
  noLabel,
  labelFontSize,
  isSmall,
  isRounded,
  isBig,
  isMulti,
}) => {
  const { t } = useTranslation();

  const formatOptionLabel = ({ label, flag }) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      {flag && (
        <img
          src={flag}
          alt={label}
          style={{ marginRight: 10, width: 20, height: 15, objectFit: "cover" }}
        />
      )}
      {label}
    </div>
  );

  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      color: "#718096", // Change this to your desired placeholder color
    }),
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: isError ? "red" : state.isFocused ? newBorder : newBorder,
      backgroundColor: white,
      border: `1px solid ${newBorder}`,
      fontSize: isSmall ? "0.875rem" : "0.938rem",
      fontWeight: "500",
      minHeight: isBig ? "4rem" : "3rem",
      height: isMulti ? "initial" : isSmall ? "2.5rem" : "3rem",
      margin: "0",
      padding: "0 8px",
      borderRadius: isRounded ? "10rem" : "0.75rem",
      boxShadow: state.isFocused ? `0 0 0 1px ${primary}` : "none",

      ":focus-visible": {
        borderColor: primary,
        boxShadow: `0 0 0 1px ${newBorder}`,
      },
      ":hover": {
        boxShadow: "none",
        outline: "none",
        cursor: "pointer",
        borderColor: "#cbd5e0",
      },
    }),

    menu: (provided) => ({
      ...provided,
      borderRadius: "0.75rem",
      boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.3)",
      border: `0px solid ${newBorder}`,
      overflow: "hidden",
      zIndex: 90000,
    }),

    menuList: (provided) => ({
      ...provided,
      margin: "0",
      padding: "0",
      border: "none",
      boxShadow: "none",
    }),

    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.5 : 1,
      color: textPrimary,
      margin: "0",
      padding: "0 0px",

      ":hover": {
        bg: "red",
      },
    }),

    option: (provided, state) => ({
      ...provided,

      // backgroundColor: state.isFocused ? backgroundLight : null,
      backgroundColor: state.isSelected ? primary : white, // Background color of selected option
      // color: state.isFocused ? textPrimary : state.data.color,
      color: state.isSelected ? "white" : textSecondary, // Color of text in options
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      margin: "0",
      padding: "0 1rem",
      fontWeight: "500",
      height: "48px",
      fontSize: "0.8rem",
      borderBottom: `1px solid ${newBorder}`,
      ":hover": {
        backgroundColor: state.isSelected
          ? shade(0.1, primary)
          : hoverItemColor,
        color: state.isSelected ? "white" : textPrimary,
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: primary,
      color: white,
      fontSize: "0.938rem",
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: state.data.color,
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      ":hover": {
        backgroundColor: state.data.bgDark,
        color: state.data.color,
      },
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontSize: "11px",
      color: textSecondary,
      fontWeight: "500",
    }),
  };

  return (
    <FormControl
      m={m}
      isRequired={isRequired}
      isInvalid={isTouched && isError}
      w={w}
      isDisabled={isDisabled}
    >
      {!noLabel && (
        <FormLabel
          fontSize={labelFontSize || "sm"}
          color={textPrimary}
          _disabled={{ opacity: 0.5 }}
        >
          {label}
          {isError && isTouched && (
            <Box position="absolute" top={-1} right={0} zIndex={2}>
              <FormErrorMessage fontSize="xs">{errorMessage}</FormErrorMessage>
            </Box>
          )}
        </FormLabel>
      )}

      <Select
        autoFocus={autoFocus}
        isDisabled={isDisabled}
        value={currencyOptions.find((option) => option.value === value)}
        onChange={onChange}
        options={currencyOptions}
        formatOptionLabel={formatOptionLabel}
        placeholder={t("placeholder.selectCurrency")}
        styles={customStyles}
      />
    </FormControl>
  );
};

export default FormControlCurrency;
